import { message } from '@client/components/_ui/feedback/message';
import { useHotkey } from '@client/hooks/useHotkey';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
    type InspectedElement,
    type InspectorState,
    SENTRY_ATTR_NAMES,
    SENTRY_ATTRS,
    type SentryAttributes,
} from './types';

/**
 * Find the nearest ancestor (or self) with Sentry attributes
 */
function findSentryAncestor(element: Element): Element | null {
    let current: Element | null = element;

    while (current) {
        if (SENTRY_ATTRS.some((attr) => current?.hasAttribute(attr))) {
            return current;
        }
        current = current.parentElement;
    }

    return null;
}

/**
 * Extract Sentry attributes from an element
 */
function extractSentryAttributes(element: Element | null): SentryAttributes {
    if (!element) {
        return {};
    }

    return {
        component: element.getAttribute(SENTRY_ATTR_NAMES.COMPONENT) ?? undefined,
        sourceFile: element.getAttribute(SENTRY_ATTR_NAMES.SOURCE_FILE) ?? undefined,
        element: element.getAttribute(SENTRY_ATTR_NAMES.ELEMENT) ?? undefined,
    };
}

/**
 * Filter out CSS-generated class names that aren't useful for code search
 */
function filterClasses(classString: string): string {
    return classString
        .split(/\s+/)
        .filter((cls) => {
            // Filter out Ant Design CSS-in-JS generated classes
            if (cls.startsWith('css-dev-only-do-not-override-')) return false;
            if (cls.startsWith('css-var-')) return false;
            if (cls.startsWith('css-')) return false;
            // Filter out hash-like classes (e.g., _abc123)
            if (/^_[a-z0-9]+$/i.test(cls)) return false;
            return cls.length > 0;
        })
        .join(' ');
}

/**
 * Get the opening tag with filtered/truncated class attribute
 */
function getOpeningTag(element: Element, truncateClass = false): string {
    const tagName = element.tagName.toLowerCase();
    const rawClass = element.getAttribute('class') ?? '';
    const filteredClass = filterClasses(rawClass);

    if (!filteredClass) {
        return `<${tagName}>`;
    }

    // Truncate long class strings
    const displayClass =
        truncateClass && filteredClass.length > 30 ? filteredClass.substring(0, 30) + '...' : filteredClass;

    return `<${tagName} class="${displayClass}">`;
}

/**
 * Block-level elements that create visual line breaks
 */
const BLOCK_ELEMENTS = new Set([
    'address',
    'article',
    'aside',
    'blockquote',
    'br',
    'dd',
    'details',
    'dialog',
    'div',
    'dl',
    'dt',
    'fieldset',
    'figcaption',
    'figure',
    'footer',
    'form',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'header',
    'hgroup',
    'hr',
    'li',
    'main',
    'nav',
    'ol',
    'p',
    'pre',
    'section',
    'summary',
    'table',
    'tr',
    'ul',
]);

/**
 * Walk the DOM tree and collect text with line breaks after block elements
 */
function collectTextWithBreaks(node: Node, texts: string[]): void {
    if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent?.trim();
        if (text) {
            texts.push(text);
        }
        return;
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        const tagName = element.tagName.toLowerCase();

        // Recurse into children
        for (const child of Array.from(node.childNodes)) {
            collectTextWithBreaks(child, texts);
        }

        // Add a line break marker after block elements
        if (BLOCK_ELEMENTS.has(tagName) && texts.length > 0) {
            // Mark end of block with a special character we'll split on
            const lastIndex = texts.length - 1;
            texts[lastIndex] = texts[lastIndex] + '\n';
        }
    }
}

/**
 * Extract text content as separate lines, preserving visual breaks from
 * block-level DOM elements
 */
function extractTextLines(element: Element, maxTotalLength = 300): string[] {
    const texts: string[] = [];
    collectTextWithBreaks(element, texts);

    // Join all texts and split on line breaks
    const combined = texts.join(' ');
    const lines = combined
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length > 0);

    // Truncate to fit within max length
    const result: string[] = [];
    let totalLength = 0;

    for (const line of lines) {
        if (totalLength >= maxTotalLength) {
            break;
        }

        const remaining = maxTotalLength - totalLength;
        if (line.length > remaining) {
            // Truncate this line and stop
            result.push(line.substring(0, remaining - 3) + '...');
            break;
        }

        result.push(line);
        totalLength += line.length;
    }

    return result;
}

/**
 * Serialize element in a compact format for AI code search:
 * <div class="...">
 *   <h3 ...>
 *   <div ...>
 *   Text content line 1
 *   Text content line 2...
 * </div>
 */
function serializeElement(element: Element): string {
    const tagName = element.tagName.toLowerCase();
    const lines: string[] = [];

    // Opening tag with class
    lines.push(getOpeningTag(element, true));

    // Direct children as placeholder tags
    const children = Array.from(element.children);
    for (const child of children.slice(0, 5)) {
        const childTag = child.tagName.toLowerCase();
        lines.push(`  <${childTag} ...>`);
    }
    if (children.length > 5) {
        lines.push(`  <!-- ${children.length - 5} more children -->`);
    }

    // Text content as separate lines
    const textLines = extractTextLines(element);
    for (const textLine of textLines) {
        lines.push(`  ${textLine}`);
    }

    // Closing tag
    lines.push(`</${tagName}>`);

    return lines.join('\n');
}

/**
 * Escape HTML special characters
 */
function _escapeHtml(text: string): string {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * Format the output for clipboard (react-grab style)
 * Example output:
 * <button class="ant-btn">Submit</button>
 * in LoginForm at components/login-form.tsx
 */
function formatOutput(clicked: Element, _sentryAncestor: Element | null, sentryAttrs: SentryAttributes): string {
    const lines: string[] = [];

    // Add just the clicked element's HTML
    lines.push(serializeElement(clicked));

    // Add component location line if we have Sentry info
    if (sentryAttrs.component || sentryAttrs.sourceFile) {
        const componentPart = sentryAttrs.component ?? 'Unknown';
        const sourcePart = sentryAttrs.sourceFile ?? 'unknown';
        lines.push(`in ${componentPart} at ${sourcePart}`);
    }

    return lines.join('\n');
}

interface UseElementInspectorReturn {
    /** Current state of the inspector */
    state: InspectorState;
    /** Currently hovered element info */
    hoveredElement: InspectedElement | null;
    /** Toggle the inspector on/off */
    toggle: () => void;
    /** Deactivate the inspector */
    deactivate: () => void;
}

/**
 * Hook for element inspection functionality
 *
 * @returns Inspector state and controls
 */
export function useElementInspector(): UseElementInspectorReturn {
    const [state, setState] = useState<InspectorState>('inactive');
    const [hoveredElement, setHoveredElement] = useState<InspectedElement | null>(null);

    // Track whether we should ignore the current click (for cleanup)
    const ignoreClickRef = useRef(false);

    const toggle = useCallback(() => {
        setState((prev) => (prev === 'inactive' ? 'active' : 'inactive'));
    }, []);

    const deactivate = useCallback(() => {
        setState('inactive');
        setHoveredElement(null);
    }, []);

    // Register hotkey - Cmd+Shift+Z (Mac) / Ctrl+Shift+Z (Windows/Linux)
    useHotkey('Cmd+Shift+Z', toggle);
    useHotkey('Ctrl+Shift+Z', toggle);
    useHotkey('Escape', deactivate, { enabled: state === 'active' });

    // Handle mouse movement
    useEffect(() => {
        if (state !== 'active') {
            setHoveredElement(null);
            return;
        }

        const handleMouseMove = (event: MouseEvent) => {
            const target = event.target as Element | null;

            if (!target || target === document.documentElement || target === document.body) {
                setHoveredElement(null);
                return;
            }

            // Ignore elements inside the inspector overlay itself
            if (target.closest('[data-element-inspector]')) {
                return;
            }

            const rect = target.getBoundingClientRect();
            const sentryAncestor = findSentryAncestor(target);
            const sentryAttributes = extractSentryAttributes(sentryAncestor);

            setHoveredElement({
                element: target,
                rect,
                sentryAttributes,
                sentryAncestor,
            });
        };

        document.addEventListener('mousemove', handleMouseMove);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
        };
    }, [state]);

    // Handle click to copy
    useEffect(() => {
        if (state !== 'active') {
            return;
        }

        const handleClick = async (event: MouseEvent) => {
            if (ignoreClickRef.current) {
                ignoreClickRef.current = false;
                return;
            }

            const target = event.target as Element | null;

            if (!target) {
                return;
            }

            // Ignore clicks on the inspector overlay itself
            if (target.closest('[data-element-inspector]')) {
                ignoreClickRef.current = true;
                return;
            }

            // Prevent default click behaviour
            event.preventDefault();
            event.stopPropagation();

            const sentryAncestor = findSentryAncestor(target);
            const sentryAttributes = extractSentryAttributes(sentryAncestor);
            const output = formatOutput(target, sentryAncestor, sentryAttributes);

            try {
                await navigator.clipboard.writeText(output);
                message.success('Element info copied to clipboard');
            } catch (error) {
                console.error('Failed to copy to clipboard:', error);
                message.error('Failed to copy to clipboard');
            }

            // Deactivate after copying
            deactivate();
        };

        // Use capture phase to intercept before other handlers
        document.addEventListener('click', handleClick, true);

        return () => {
            document.removeEventListener('click', handleClick, true);
        };
    }, [state, deactivate]);

    // Add cursor style when active
    useEffect(() => {
        if (state === 'active') {
            document.body.style.cursor = 'crosshair';
        } else {
            document.body.style.cursor = '';
        }

        return () => {
            document.body.style.cursor = '';
        };
    }, [state]);

    return {
        state,
        hoveredElement,
        toggle,
        deactivate,
    };
}
